<template>
	<v-app-bar
		app
		color="primary"
		dark
		height="50"
		elevation="1"
	>
        <v-app-bar-nav-icon @click="$emit('drawer')" class="me-2"></v-app-bar-nav-icon>
        <h1 class="text-uppercase text-body-1">{{$T(`routes.${$route.meta.i18nTranslateKey}`)}}</h1>
		<v-spacer/>

		<v-tooltip bottom>
			<template v-slot:activator="{ on, attrs }">
				<span
					v-bind="attrs"
					v-on="on"
					class="internet-status-dot me-1"
					:class="isOnline ? 'internet-status-dot--online' : 'internet-status-dot--offline'"
					:aria-label="$T('internet-quality')"
				></span>
			</template>
			<div class="internet-status-tooltip">
				<div class="text-body-2">{{ connectionStatusText }}</div>
				<div class="caption">{{ connectionDetailsText }}</div>
			</div>
		</v-tooltip>

		<v-btn
			small
			text
			@click="changeI18nLang($i18n.locale !== 'ar' ? 'ar' : 'en')"
		>
			{{$i18n.locale !== 'ar' ? 'العربية' : 'English'}}
			<v-icon size="18" class="ms-2">mdi-earth</v-icon>
		</v-btn>

		<v-badge
			v-if="useNotifications"
			id="notification-badge"
			color="red"
			overlap
			:content="notifications.length"
			:value="notifications.length"
			:offset-x="12"
			:offset-y="12"
		>
			<v-btn
				small
				icon
				class="ms-1"
				:to="{ name: 'notifications' }"
			>
				<v-icon size="18">mdi-bell{{notifications.length ? '-ring' : ''}}</v-icon>
			</v-btn>
		</v-badge>
		
		<!-- loading line -->
		<v-progress-linear
			indeterminate
			absolute
			bottom
			:active="loading"
			height="4"
			background-color="primary"
			color="secondary lighten-1"
		></v-progress-linear>
    </v-app-bar>
</template>

<script>
import { changeI18nLang } from '@/i18n/i18n'
import { mapState } from 'vuex'
import { useNotifications } from '@/configs/general.configs'
export default {
	props: [
		'loading'
	],

	data() {
		return {
			useNotifications,
			isOnline: navigator.onLine,
			latency: null,
			quality: 'excellent',
			networkPingIntervalId: null,
		}
	},

	computed: {
		...mapState({
			notifications: state => state.notifications.notifications
		}),

		connectionStatusText() {
			return this.$T(this.isOnline ? 'connected' : 'disconnected')
		},

		connectionDetailsText() {
			if (!this.isOnline || this.latency === null) {
				return this.$T('internet-quality-detail-disconnected')
			}

			return this.$T('internet-quality-detail-connected', {
				quality: this.$T(`internet-quality-levels.${this.quality}`),
				latency: this.latency,
			})
		}
	},

	methods: {
		changeI18nLang,

		getConnectionQuality(latency) {
			if (latency <= 150) return 'excellent'
			if (latency <= 350) return 'good'
			return 'weak'
		},

		checkInternetQuality() {
			if (!navigator.onLine) {
				this.isOnline = false
				this.latency = null
				return Promise.resolve()
			}

			const startedAt = performance.now()
			return fetch('/favicon.ico', {
				method: 'HEAD',
				cache: 'no-store',
			}).then(() => {
				const measuredLatency = Math.round(performance.now() - startedAt)
				this.isOnline = true
				this.latency = measuredLatency
				this.quality = this.getConnectionQuality(measuredLatency)
			}).catch((error) => {
				if (error && error.response) {
					const measuredLatency = Math.round(performance.now() - startedAt)
					this.isOnline = true
					this.latency = measuredLatency
					this.quality = this.getConnectionQuality(measuredLatency)
					return
				}

				this.isOnline = false
				this.latency = null
			})
		},

		handleOnline() {
			this.isOnline = true
			this.checkInternetQuality()
		},

		handleOffline() {
			this.isOnline = false
			this.latency = null
		}
	},

	mounted() {
		window.addEventListener('online', this.handleOnline)
		window.addEventListener('offline', this.handleOffline)

		this.checkInternetQuality()
		this.networkPingIntervalId = setInterval(this.checkInternetQuality, 10000)
	},

	beforeDestroy() {
		if (this.networkPingIntervalId) {
			clearInterval(this.networkPingIntervalId)
			this.networkPingIntervalId = null
		}

		window.removeEventListener('online', this.handleOnline)
		window.removeEventListener('offline', this.handleOffline)
	}
}
</script>

<style lang="scss">
#notification-badge {
	.v-badge__badge {
		display: flex;
		justify-content: center;
		align-items: center;
		min-width: 14px;
		width: 14px;
		height: 14px;
		padding: 0;
	}
}

.internet-status-dot {
	display: inline-block;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	cursor: pointer;
	transition: transform 0.2s ease, box-shadow 0.2s ease;

	&:hover {
		transform: scale(1.12);
	}
}

.internet-status-dot--online {
	background-color: #2ecc71;
}

.internet-status-dot--offline {
	background-color: #b0b0b0;
}

.internet-status-tooltip {
	line-height: 1.3;
}
</style>